import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Globe, CheckCircle, BookOpen, Calendar, Paperclip, NotebookPen, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import AddBreedingEventModal from './AddBreedingEventModal';
import CoverRecordDialog from './CoverRecordDialog';
import QuickLogModal from '../farmfolio/QuickLogModal';
import ScheduleCareModal from '../farmfolio/ScheduleCareModal';
import TravelLogModal from '../farmfolio/TravelLogModal';
import CareLogEntryDetailModal from '../farmfolio/CareLogEntryDetailModal';
import ApplyProtocolModal from '../farmfolio/ApplyProtocolModal';
import CareCatalogPanel from '../farmfolio/CareCatalogPanel';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const FOALING_MILESTONES = ['Water Broke','Feet Appeared','Delivery','Stood','Nursed','Placenta Passed'];

const EVENT_COLORS = {
  tease_score:         'bg-yellow-100 text-yellow-800 border-yellow-300',
  palpation:           'bg-blue-100 text-blue-800 border-blue-300',
  ovulation_confirmed: 'bg-orange-100 text-orange-800 border-orange-300',
  bred:                'bg-green-100 text-green-800 border-green-300',
  pregnant:            'bg-pink-100 text-pink-800 border-pink-300',
};
const EVENT_SHORT = { tease_score:'TS', palpation:'PR', ovulation_confirmed:'OC', bred:'B', pregnant:'P' };

function fmtDate(d) {
  if (!d) return '';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmt12(t) {
  if (!t) return '';
  const [h,m] = t.split(':').map(Number);
  return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`;
}
function fmtScheduledName(item) {
  const nameStr = item.name || '';
  const typeStr = item.type || '';
  const base = nameStr.toLowerCase().startsWith(typeStr.toLowerCase()) ? nameStr : typeStr ? `${typeStr} — ${nameStr}` : nameStr;
  const dosage = item.dosage ? ` (${item.dosage}${item.dosage_unit ? ' ' + item.dosage_unit : ''})` : '';
  return base + dosage;
}

const CARE_BORDER = {
  'Medical':      'border-l-4 border-l-purple-300',
  'Daily Care':   'border-l-4 border-l-sky-300',
  'Maintenance':  'border-l-4 border-l-yellow-300',
  'Conditioning': 'border-l-4 border-l-emerald-300',
  'Travel':       'border-l-4 border-l-indigo-300',
};

function CareSection({ title, items, onAddCare, onAddProtocol, completeScheduled }) {
  return (
    <div className="border rounded p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{title}</h4>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onAddCare}>+ Add Care</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onAddProtocol}>+ Add Protocol</Button>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No {title.toLowerCase()} care scheduled.</p>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id}
              className={`flex items-center justify-between p-2.5 border rounded hover:bg-muted/20 ${CARE_BORDER[item.category] || 'border-l-4 border-l-gray-300'}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {item.type} — {item.name}
                  {item.dosage ? ` (${item.dosage}${item.dosage_unit ? ' ' + item.dosage_unit : ''})` : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  {item.next_due_date ? `Due: ${fmtDate(item.next_due_date)}` : 'No date'}
                  {item.preferred_time && ` @ ${fmt12(item.preferred_time)}`}
                  {item.location_name && ` · ${item.location_name}`}
                </p>
              </div>
              <Button size="sm" className="h-7 text-xs border border-primary text-primary hover:opacity-90 shrink-0 ml-2"
                style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}
                onClick={() => completeScheduled(item)}>
                <CheckCircle className="w-3 h-3 mr-1" /> Complete
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BreedingSeasonContent({ season, horse, locations, catalogItems, onSeasonUpdated, onCoverAdded, onPregnancyChanged, isFarm }) {
  const [events, setEvents]                 = useState([]);
  const [foaling, setFoaling]               = useState(null);
  const [careLog, setCareLog]               = useState([]);
  const [scheduledCare, setScheduled]       = useState([]);
  const [activeMonth, setActiveMonth] = useState(() => {
    const months = season.months?.length > 0 ? season.months : MONTHS;
    const currentYear  = new Date().getFullYear();
    const currentMIdx  = new Date().getMonth();
    const currentMStr  = MONTHS[currentMIdx];
    if (season.year < currentYear) return months[months.length - 1];
    if (season.year > currentYear) return months[0];
    if (months.includes(currentMStr)) return currentMStr;
    const pastMonths = months.filter(m => MONTHS.indexOf(m) < currentMIdx);
    if (pastMonths.length === months.length) return months[months.length - 1];
    return months[0];
  });
  const [eventModal, setEventModal]                 = useState({ open: false, type: null, editing: null });
  const [coverDialogOpen, setCoverDialogOpen]       = useState(false);
  const [coverDialogRecord, setCoverDialogRecord]   = useState(null);
  const [coverDialogInitialDate, setCoverDialogInitialDate] = useState(null);
  const [coverDialogShowTypeSelector, setCoverDialogShowTypeSelector] = useState(false);
  const [travelModal, setTravelModal]               = useState({ open: false, item: null });
  const [quickLogModal, setQuickLogModal]           = useState({ open: false });
  const [schedCareModal, setSchedCareModal]         = useState({ open: false });
  const [showCatalog, setShowCatalog]               = useState(false);
  const [protocolPickerOpen, setProtocolPickerOpen] = useState(false);
  const [selectedProtocolId, setSelectedProtocolId] = useState('');
  const [applyModal, setApplyModal]                 = useState({ open: false, protocol: null });
  const [careLogDetail, setCareLogDetail]           = useState(null);
  const [protocols, setProtocols]           = useState([]);
  const [foalingDeliveryModal, setFoalingDeliveryModal] = useState(false);
  const [foalSex, setFoalSex]               = useState('');
  const [deliveryPregnancyStatus, setDeliveryPregnancyStatus] = useState('');
  const [deliveryDate, setDeliveryDate]     = useState('');
  const [deliveryTime, setDeliveryTime]     = useState('');
  const [createFoalProfile, setCreateFoalProfile] = useState(false);
  const [loading, setLoading]               = useState(true);
  const [pregInfo, setPregInfo]             = useState(null); // cross-season pregnancy info

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch each independently so one failure doesn't block others
      const [evRes, foalRes, clRes, scRes, prRes, pregInfoRes] = await Promise.allSettled([
        api.get(`/breeding-seasons/events/list?season_id=${season.id}`),
        api.get(`/breeding-seasons/foaling/${season.id}`),
        api.get(`/farmfolio/care-log?horse_id=${horse.id}`),
        api.get(`/farmfolio/scheduled-care?horse_id=${horse.id}`),
        api.get('/farmfolio/protocols'),
        api.get(`/breeding-seasons/pregnancy-info/${horse.id}`),
      ]);
      setEvents(evRes.status === 'fulfilled' ? (evRes.value.data || []) : []);
      setFoaling(foalRes.status === 'fulfilled' ? foalRes.value.data : null);
      setCareLog(clRes.status === 'fulfilled' ? (clRes.value.data || []).filter(e => !!e.is_breeding) : []);
      setScheduled(scRes.status === 'fulfilled' ? (scRes.value.data || []).filter(s => !!s.is_breeding && s.is_active !== false) : []);
      setProtocols(prRes.status === 'fulfilled' ? (prRes.value.data || []) : []);
      // Issue 6: store cross-season pregnancy info
      setPregInfo(pregInfoRes.status === 'fulfilled' ? pregInfoRes.value.data : null);
    } catch (e) { console.warn('BreedingSeasonContent fetch error', e); }
    setLoading(false);
  }, [season.id, horse.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const eventsByDate = {};
  events.forEach(ev => {
    if (!eventsByDate[ev.event_date]) eventsByDate[ev.event_date] = [];
    eventsByDate[ev.event_date].push(ev);
  });

  // Pregnant event for P tile persistence — prefer unresolved; fall back to any
  const pregnantEvent = events.find(e => e.event_type === 'pregnant' && !e.resolved_status)
    || events.find(e => e.event_type === 'pregnant');
  const deliveryTicked = (foaling?.milestones || []).find(m => m.name === 'Delivery')?.checked;
  const isActivelyPregnant = !!pregnantEvent && (!pregnantEvent.resolved_status || pregnantEvent.resolved_status === 'Sold');

  // Issue 6: Cross-year pregnancy — use prior_season_pregnancy from the endpoint when available.
  // Falls back to main pregInfo when no prior-season pregnancy exists.
  const crossYearSource = pregInfo?.prior_season_pregnancy || null;
  const crossYearPregnancy = crossYearSource && crossYearSource.pregnant_since
    && parseInt((crossYearSource.pregnant_since || '').slice(0, 4), 10) < season.year
    ? crossYearSource
    : null;

  // Calendar
  const buildCalendar = () => {
    if (!activeMonth) return [];
    const monthIdx = MONTHS.indexOf(activeMonth);
    const firstDay = new Date(season.year, monthIdx, 1).getDay();
    const daysInMonth = new Date(season.year, monthIdx + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  };

  const dateStr = (day) => {
    const m = String(MONTHS.indexOf(activeMonth) + 1).padStart(2, '0');
    return `${season.year}-${m}-${String(day).padStart(2, '0')}`;
  };

  const openCoverForBredEvent = async (ev) => {
    try {
      const res = await api.get(`/cover-records?horse_id=${horse.id}`);
      const records = res.data || [];
      const eventDate = ev.event_date;
      const match = records.find(r => r.cover_date && r.cover_date.startsWith(eventDate))
        || records.find(r => r.covering_sire === ev.data?.covering_sire);
      setCoverDialogRecord(match || null);
    } catch {
      setCoverDialogRecord(null);
    }
    setCoverDialogOpen(true);
  };

  const [editMilestoneModal, setEditMilestoneModal] = useState(null); // { name, date, time }

  const openMilestoneEdit = (ms) => {
    // For "Delivery" — open the full foaling delivery dialog pre-populated
    if (ms.name === 'Delivery') {
      const dt = ms.datetime ? new Date(ms.datetime) : new Date();
      setDeliveryDate(dt.toLocaleDateString('sv'));
      setDeliveryTime(dt.toTimeString().slice(0, 5));
      setFoalSex(foaling?.foal_sex || '');
      setDeliveryPregnancyStatus(foaling?.delivery_pregnancy_status || '');
      setCreateFoalProfile(!!foaling?.foal_horse_id);
      setFoalingDeliveryModal(true);
      return;
    }
    // For all other milestones — open the simple date/time edit
    const dt = ms.datetime ? new Date(ms.datetime) : new Date();
    setEditMilestoneModal({
      name: ms.name,
      date: dt.toLocaleDateString('sv'),
      time: dt.toTimeString().slice(0, 5),
    });
  };

  const saveMilestoneEdit = async () => {
    if (!editMilestoneModal || !foaling) return;
    const { name, date, time } = editMilestoneModal;
    const newDatetime = new Date(`${date}T${time || '00:00'}:00`).toISOString();
    const updated = (foaling.milestones || []).map(m =>
      m.name === name ? { ...m, datetime: newDatetime } : m
    );
    const res = await api.put(`/breeding-seasons/foaling/${season.id}`, { horse_id: horse.id, milestones: updated });
    setFoaling(res.data);
    setEditMilestoneModal(null);
  };

  const toggleMilestone = async (name, checked) => {
    if (!foaling) return;
    const updated = (foaling.milestones || FOALING_MILESTONES.map(n => ({ name: n, checked: false, datetime: null }))).map(m =>
      m.name === name ? { ...m, checked, datetime: checked ? new Date().toISOString() : null } : m
    );
    if (name === 'Delivery' && checked) {
      const now = new Date();
      setDeliveryDate(now.toLocaleDateString('sv'));
      setDeliveryTime(now.toTimeString().slice(0, 5));
      setDeliveryPregnancyStatus('');
      setFoalSex('');
      setCreateFoalProfile(false);
      setFoaling(f => ({ ...f, milestones: updated }));
      setFoalingDeliveryModal(true);
      return;
    }
    const res = await api.put(`/breeding-seasons/foaling/${season.id}`, { horse_id: horse.id, milestones: updated });
    setFoaling(res.data);
    // Issue 5: Re-open delivery modal when all milestones are checked and outcome not yet recorded
    const allChecked = updated.every(m => m.checked);
    if (allChecked && !res.data?.foal_sex) {
      const now = new Date();
      setDeliveryDate(now.toLocaleDateString('sv'));
      setDeliveryTime(now.toTimeString().slice(0, 5));
      setDeliveryPregnancyStatus('');
      setFoalSex('');
      setCreateFoalProfile(false);
      setFoalingDeliveryModal(true);
    }
    // Refresh pregnancy header badge and pregInfo when delivery is ticked
    if (name === 'Delivery' && checked) {
      onPregnancyChanged?.();
      fetchAll();
    }
  };

  const saveFoalDelivery = async () => {
    const base = foaling?.milestones || FOALING_MILESTONES.map(n => ({ name: n, checked: false, datetime: null }));
    const deliveryDatetime = deliveryDate
      ? new Date(`${deliveryDate}T${deliveryTime || '00:00'}:00`).toISOString()
      : new Date().toISOString();
    const updated = base.map(m =>
      m.name === 'Delivery' ? { ...m, checked: true, datetime: deliveryDatetime, foal_sex: foalSex } : m
    );
    try {
      const res = await api.put(`/breeding-seasons/foaling/${season.id}`, {
        horse_id: horse.id,
        milestones: updated,
        foal_sex: foalSex,
        delivery_pregnancy_status: deliveryPregnancyStatus,
        create_foal_profile: createFoalProfile && deliveryPregnancyStatus === 'Live Foal',
        sire_name: season.covering_sires || '',
      });
      setFoaling(res.data);
      if (res.data?.foal_horse_id) {
        toast.success('Live foal profile created successfully!');
      }
      onPregnancyChanged?.();
      onCoverAdded?.();   // refresh cover history to show updated pregnancy outcome
      fetchAll();         // refresh pregInfo so pregnancy badge + P chips update immediately
    } catch {
      toast.error('Failed to save delivery details');
    }
    setFoalingDeliveryModal(false);
  };

  const completeScheduled = async (item) => {
    // Travel items open TravelLogModal instead of completing inline
    if (item.category === 'Travel') {
      setTravelModal({ open: true, item });
      return;
    }
    try {
      const res = await api.post(`/farmfolio/scheduled-care/${item.id}/complete`, {
        log_date: new Date().toLocaleDateString('sv'),
        log_time: new Date().toTimeString().slice(0, 5),
      });
      // Immediately show the new entry in the breeding care log, then full refresh
      const newEntry = res?.data;
      if (newEntry?.id) {
        setCareLog(prev => [newEntry, ...prev.filter(e => e.id !== newEntry.id)].filter(e => !!e.is_breeding));
      }
      toast.success('Care completed');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <p className="text-sm text-center py-4 text-muted-foreground">Loading…</p>;

  const localToday    = new Date().toLocaleDateString('sv');
  const todayCare     = scheduledCare.filter(s => s.next_due_date && s.next_due_date <= localToday);
  const upcomingCare  = scheduledCare.filter(s => !s.next_due_date || s.next_due_date > localToday);

  // Only breeding catalog items and protocols
  const breedingCatalogItems = (catalogItems || []).filter(c => c.is_breeding);
  const breedingCatalogIds   = new Set(breedingCatalogItems.map(c => c.id));
  const breedingProtocols    = protocols.filter(p => p.is_breeding);

  const CareSectionProps = {
    onAddCare:       () => setSchedCareModal({ open: true }),
    onAddProtocol:   () => { setSelectedProtocolId(''); setProtocolPickerOpen(true); },
    completeScheduled,
  };

  const milestones = foaling?.milestones || FOALING_MILESTONES.map(n => ({ name: n, checked: false, datetime: null }));

  return (
    <div className="space-y-4 pt-4">
      {/* Care Catalog panel — rendered full-width when open */}
      {showCatalog && (
        <CareCatalogPanel
          onClose={() => setShowCatalog(false)}
          onSaved={() => { fetchAll(); setShowCatalog(false); }}
          horse={horse}
        />
      )}

      {/* Management buttons — match FarmFolio tab style */}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="h-8 text-xs"
          onClick={() => setShowCatalog(v => !v)}>
          <BookOpen className="w-3 h-3 mr-1" /> Care Catalog
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs"
          onClick={() => setSchedCareModal({ open: true })}>
          <Calendar className="w-3 h-3 mr-1" /> Manage Scheduled Care
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT — single care card + foaling */}
        <div className="space-y-4">
          <div className="border rounded overflow-hidden">
            {/* Fixed header — title + buttons */}
            <div className="flex items-center justify-between px-3 py-2 border-b bg-white">
              <h4 className="text-sm font-semibold">Breeding Care</h4>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSchedCareModal({ open: true })}>+ Add Care</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setSelectedProtocolId(''); setProtocolPickerOpen(true); }}>+ Add Protocol</Button>
              </div>
            </div>

            {/* Scrollable content — matches FarmFolioTab care card */}
            <div className="p-3 space-y-4" style={{ height: '460px', overflowY: 'auto' }}>

              {/* Today's Care */}
              <div>
                <p className="text-sm font-semibold mb-2">Today's Care</p>
                {todayCare.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-2 text-center">No breeding care due today.</p>
                ) : (
                  <div className="space-y-2">
                    {todayCare.map(item => (
                      <div key={item.id}
                        className={`flex items-center justify-between p-3 border rounded hover:bg-muted/20 ${CARE_BORDER[item.category] || 'border-l-4 border-l-gray-300'}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{fmtScheduledName(item)}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.preferred_time && `@ ${fmt12(item.preferred_time)}`}
                            {item.location_name && ` · ${item.location_name}`}
                          </p>
                        </div>
                        <Button size="sm" className="h-7 text-xs border border-primary text-primary hover:opacity-90 shrink-0 ml-2"
                          style={{ backgroundColor: 'rgba(52,107,78,0.1)' }}
                          onClick={() => completeScheduled(item)}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Complete
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Care in Next 30 Days */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-sm font-semibold">Care in Next 30 Days</p>
                  {upcomingCare.length > 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">{upcomingCare.length}</span>
                  )}
                </div>
                {upcomingCare.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-2 text-center">No upcoming breeding care scheduled.</p>
                ) : (
                  <div className="space-y-2">
                    {upcomingCare.map(item => (
                      <div key={item.id}
                        className={`flex items-center justify-between p-2.5 border rounded hover:bg-muted/20 ${CARE_BORDER[item.category] || 'border-l-4 border-l-gray-300'}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{fmtScheduledName(item)}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.next_due_date && `Due: ${fmtDate(item.next_due_date)}`}
                            {item.preferred_time && ` @ ${fmt12(item.preferred_time)}`}
                            {item.location_name && ` · ${item.location_name}`}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 text-xs shrink-0 ml-2"
                          onClick={() => completeScheduled(item)}>
                          Complete
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Foaling — only shown once mare is confirmed pregnant */}
          {isActivelyPregnant && (
          <div className="border rounded p-3 space-y-2">
            <h4 className="text-xs font-semibold">Foaling</h4>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Delivery Milestones:</p>
            <div className="space-y-1.5">
              {milestones.map((ms, i) => (
                <div key={ms.name} className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={!!ms.checked}
                      onCheckedChange={checked => toggleMilestone(ms.name, checked)}
                      id={`ms-${season.id}-${i}`}
                    />
                    <label htmlFor={`ms-${season.id}-${i}`} className="text-xs cursor-pointer text-muted-foreground flex-1">
                      {i + 1}. {ms.name}
                    </label>
                    {ms.checked && ms.datetime && (
                      <span className="text-[10px] font-semibold text-foreground ml-auto shrink-0 flex items-center gap-1">
                        {new Date(ms.datetime).toLocaleDateString('en-US', { month:'short', day:'numeric' })}
                        {' '}{fmt12(new Date(ms.datetime).toTimeString().slice(0,5))}
                        {ms.name === 'Delivery' && ms.foal_sex && (
                          <span className="ml-1 text-muted-foreground">({ms.foal_sex === 'm' ? 'Colt' : 'Filly'})</span>
                        )}
                        <button
                          className="ml-1 text-muted-foreground hover:text-primary"
                          title="Edit date/time"
                          onClick={e => { e.stopPropagation(); openMilestoneEdit(ms); }}
                        >
                          <Pencil className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {foaling?.foal_sex && (
              <p className="text-[10px] border-t pt-1.5">
                Foal: <span className="font-semibold">{foaling.foal_sex === 'm' ? 'Colt' : 'Filly'}</span>
              </p>
            )}
          </div>
          )}
        </div>

        {/* RIGHT — Event action buttons + Calendar */}
        <div className="space-y-3">
          {/* Action buttons ABOVE calendar */}
          <div className="flex flex-wrap gap-1.5">
            {[
              { type: 'tease_score',         label: 'Add Tease Score' },
              { type: 'palpation',           label: 'Add Palpation Record' },
              { type: 'ovulation_confirmed', label: 'Ovulation Confirmed' },
            ].map(({ type, label }) => (
              <Button key={type} size="sm"
                className="bg-primary hover:bg-primary/90 text-white text-xs h-7"
                onClick={() => setEventModal({ open: true, type, editing: null })}>
                {label}
              </Button>
            ))}
            <Button size="sm"
              className="bg-primary hover:bg-primary/90 text-white text-xs h-7"
              onClick={() => {
                setCoverDialogRecord(null);
                setCoverDialogInitialDate(null);
                setCoverDialogShowTypeSelector(false);
                setCoverDialogOpen(true);
              }}>
              Bred
            </Button>
            <Button size="sm"
              className="bg-primary hover:bg-primary/90 text-white text-xs h-7"
              onClick={() => setEventModal({ open: true, type: 'pregnant', editing: null })}>
              Pregnant
            </Button>
          </div>

          {/* Month tabs */}
          <div className="flex flex-wrap gap-1">
            {(season.months?.length > 0 ? season.months : MONTHS).map(m => (
              <button key={m} type="button" onClick={() => setActiveMonth(m)}
                className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                  activeMonth === m ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}>{m}</button>
            ))}
          </div>

          {/* Calendar */}
          {activeMonth && (
            <div className="border rounded overflow-hidden">
              <div className="grid grid-cols-7 bg-muted/20">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                  <div key={d} className="text-center text-xs font-semibold py-1.5 border-b">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {buildCalendar().map((day, i) => {
                  const ds = day ? dateStr(day) : null;
                  const dayEvs = ds ? (eventsByDate[ds] || []) : [];
                  // P tile persistence: stop chips at delivery date, horse sold_date, or today
                  // soldDate now authoritative from horse record — not from pregnancy event resolved_status
                  const soldDate = (horse.sale_status === 'Sold' && !horse.keep_in_care)
                    ? (horse.sold_date || null)
                    : null;
                  const deliveryMilestone = (foaling?.milestones || []).find(m => m.name === 'Delivery' && m.checked);
                  const deliveryDateStr = deliveryMilestone?.datetime ? deliveryMilestone.datetime.slice(0, 10) : null;
                  const pChipCutoff = deliveryDateStr || soldDate || localToday;
                  const showPersistentP = !!pregnantEvent && (!pregnantEvent.resolved_status || (soldDate && ds <= soldDate))
                    && ds && ds >= pregnantEvent.event_date
                    && ds <= pChipCutoff
                    && !dayEvs.find(e => e.event_type === 'pregnant');
                  // Issue 6: Cross-year pregnancy chip — show P from Jan 1 of this season up to cutoff
                  const crossYearCutoff = crossYearPregnancy?.delivered_date || crossYearPregnancy?.estimated_due_date;
                  const showCrossYearP = !!crossYearPregnancy && ds
                    && ds >= `${season.year}-01-01`
                    && (!crossYearCutoff || ds <= crossYearCutoff)
                    && !dayEvs.find(e => e.event_type === 'pregnant')
                    && !showPersistentP;
                  return (
                    <div key={i}
                      className={`min-h-[64px] border-r border-b p-1 text-xs ${day ? 'cursor-pointer hover:bg-muted/10' : 'bg-muted/5'}`}
                      onClick={() => day && setEventModal({ open: true, type: null, editing: null, date: ds })}>
                      {day && (
                        <>
                          <span className="text-[10px] text-muted-foreground">{day}</span>
                          <div className="mt-0.5 flex flex-wrap gap-0.5">
                            {dayEvs.map((ev, ei) => (
                              <div key={ei}
                                className={`px-1 py-0.5 rounded text-[9px] font-bold border cursor-pointer ${EVENT_COLORS[ev.event_type] || 'bg-gray-100 text-gray-700 border-gray-300'}`}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (ev.event_type === 'bred') { openCoverForBredEvent(ev); }
                                  else { setEventModal({ open: true, type: ev.event_type, editing: ev }); }
                                }}
                                title={ev.event_type}>
                                {EVENT_SHORT[ev.event_type]}
                                {ev.event_type === 'tease_score' && ev.data?.score && <span> {ev.data.score}</span>}
                                {ev.event_type === 'palpation' && ev.data?.has_attachments && <Paperclip className="w-2 h-2 inline ml-0.5" />}
                                {ev.event_type === 'palpation' && ev.data?.has_note && <NotebookPen className="w-2 h-2 inline ml-0.5" />}
                              </div>
                            ))}
                            {showPersistentP && (
                              <div
                                className={`px-1 py-0.5 rounded text-[9px] font-bold border opacity-60 cursor-pointer ${EVENT_COLORS.pregnant}`}
                                title={soldDate && ds === soldDate
                                    ? `Sold as of ${new Date(soldDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. No further records available.`
                                    : 'Pregnant (ongoing) — click to edit'}
                                onClick={e => { e.stopPropagation(); setEventModal({ open: true, type: 'pregnant', editing: pregnantEvent, tileDate: ds }); }}
                              >P</div>
                            )}
                            {showCrossYearP && (
                              <div
                                className={`px-1 py-0.5 rounded text-[9px] font-bold border opacity-40 ${EVENT_COLORS.pregnant}`}
                                title={`Pregnant from ${crossYearPregnancy.pregnant_since ? new Date(crossYearPregnancy.pregnant_since + 'T00:00:00').getFullYear() : 'prior season'}${crossYearPregnancy.estimated_due_date ? ` · Due ${new Date(crossYearPregnancy.estimated_due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}`}
                              >P</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="flex flex-wrap gap-1.5 text-[10px]">
            {Object.entries(EVENT_SHORT).map(([type, short]) => (
              <div key={type} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-medium ${EVENT_COLORS[type]}`}>
                <span className="font-bold">{short}</span>
                <span>{type.replace(/_/g,' ')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Breeding Care Log — pre-filtered, no pills needed ── */}
      <div>
        <h4 className="text-sm font-semibold mb-2">Breeding Care Log</h4>
        {careLog.length === 0 ? (
          <p className="text-xs text-muted-foreground italic py-2">No breeding care logged yet.</p>
        ) : (
          <div className="overflow-x-auto border rounded">
            <table className="w-full text-xs">
              <thead className="bg-muted/10 border-b">
                <tr>
                  <th className="text-left p-2 font-medium">Category</th>
                  <th className="text-left p-2 font-medium">Description</th>
                  <th className="text-left p-2 font-medium">Date / Time</th>
                  <th className="text-left p-2 font-medium">By</th>
                  <th className="text-left p-2 font-medium">Location</th>
                  <th className="text-left p-2 font-medium">Comments</th>
                </tr>
              </thead>
              <tbody>
                {careLog.map(e => {
                  const nameStr = e.name || '';
                  const typeStr = e.type || '';
                  const desc = e.category === 'Stats'
                    ? nameStr
                    : nameStr.toLowerCase().startsWith(typeStr.toLowerCase())
                      ? nameStr
                      : typeStr ? `${typeStr} — ${nameStr}` : nameStr;
                  return (
                    <tr key={e.id} className="border-b last:border-0 hover:bg-muted/20 cursor-pointer"
                      onClick={() => setCareLogDetail(e)}>
                      <td className="p-2">{e.category}</td>
                      <td className="p-2 font-medium">{desc}</td>
                      <td className="p-2 whitespace-nowrap">
                        {e.log_date}
                        {e.log_time && ` @ ${fmt12(e.log_time)}`}
                      </td>
                      <td className="p-2">{e.performed_by || '—'}</td>
                      <td className="p-2">{e.location_name || '—'}</td>
                      <td className="p-2 max-w-[120px] truncate">
                        {e.publish_to_owner_portal && <Globe className="w-3 h-3 text-blue-500 inline mr-1" />}
                        {e.comments || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddBreedingEventModal
        open={eventModal.open}
        onClose={() => setEventModal({ open: false, type: null, editing: null })}
        onSaved={(savedType) => {
          fetchAll();
          setEventModal({ open: false, type: null, editing: null });
          // Refresh header badge whenever a pregnant event is saved (any path)
          if (savedType === 'pregnant' || eventModal.type === 'pregnant' || eventModal.editing?.event_type === 'pregnant') {
            onPregnancyChanged?.();
            onCoverAdded?.(); // refresh cover history to show updated "In Foal" outcome immediately
          }
        }}
        seasonId={season.id}
        horseId={horse.id}
        initialType={eventModal.type}
        initialDate={eventModal.date}
        tileDate={eventModal.tileDate}
        editing={eventModal.editing}
        isFarm={isFarm}
        horseSoldInfo={{ is_sold: horse.sale_status === 'Sold', sold_date: horse.sold_date || null, keep_in_care: !!horse.keep_in_care }}
        onBredSelected={() => {
          // "Bred" selected from calendar-day dropdown → open CoverRecordDialog with the clicked date + type selector
          const clickedDate = eventModal.date;
          setEventModal({ open: false, type: null, editing: null });
          setCoverDialogRecord(null);
          setCoverDialogInitialDate(clickedDate || null);
          setCoverDialogShowTypeSelector(true);
          setCoverDialogOpen(true);
        }}
      />

      {/* Travel Log Modal — for completing scheduled travel */}
      <TravelLogModal
        open={travelModal.open}
        onClose={() => setTravelModal({ open: false, item: null })}
        onSaved={() => { fetchAll(); setTravelModal({ open: false, item: null }); }}
        horse={horse}
        locations={locations || []}
        scheduledItem={travelModal.item}
      />

      {/* Quick Log — for logging immediate care (future use) */}
      <QuickLogModal        open={quickLogModal.open}
        onClose={() => setQuickLogModal({ open: false })}
        onSaved={() => { fetchAll(); setQuickLogModal({ open: false }); }}
        horse={horse}
        locations={locations || []}
        catalogItems={catalogItems || []}
        prefill={{ category: 'Medical', is_breeding: true }}
      />

      {/* Schedule Care — breeding catalog items only */}
      <ScheduleCareModal
        open={schedCareModal.open}
        onClose={() => setSchedCareModal({ open: false })}
        onSaved={() => { fetchAll(); setSchedCareModal({ open: false }); }}
        horse={horse}
        locations={locations || []}
        catalogItems={breedingCatalogItems}
      />

      {/* Protocol picker — breeding protocols only */}
      <Dialog open={protocolPickerOpen} onOpenChange={setProtocolPickerOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Select Protocol</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Select value={selectedProtocolId || '__none__'} onValueChange={v => setSelectedProtocolId(v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Choose a protocol…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select —</SelectItem>
                {breedingProtocols.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {breedingProtocols.length === 0 && (
              <p className="text-xs text-muted-foreground italic">No breeding protocols found. Create a protocol using only breeding care items to see it here.</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setProtocolPickerOpen(false)}>Cancel</Button>
            <Button
              disabled={!selectedProtocolId}
              className="bg-primary hover:bg-primary/90 text-white"
              onClick={() => {
                const p = breedingProtocols.find(x => x.id === selectedProtocolId);
                if (p) { setApplyModal({ open: true, protocol: p }); setProtocolPickerOpen(false); }
              }}>
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ApplyProtocolModal
        open={applyModal.open}
        onClose={() => setApplyModal({ open: false, protocol: null })}
        onSaved={() => { fetchAll(); setApplyModal({ open: false, protocol: null }); }}
        protocol={applyModal.protocol}
        availableHorses={[]}
        preselectedHorse={horse}
      />

      {/* Care log entry detail / edit */}
      <CareLogEntryDetailModal
        open={!!careLogDetail}
        onClose={() => setCareLogDetail(null)}
        entry={careLogDetail}
        locations={locations || []}
        onDeleted={(id) => { setCareLog(prev => prev.filter(e => e.id !== id)); setCareLogDetail(null); }}
        onUpdated={(updated) => { fetchAll(); setCareLogDetail(updated); }}
      />

      {/* Foaling delivery detail prompt */}

      {/* Edit Milestone Date/Time Dialog */}
      <Dialog open={!!editMilestoneModal} onOpenChange={v => !v && setEditMilestoneModal(null)}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle>Edit — {editMilestoneModal?.name}</DialogTitle>
          </DialogHeader>
          {editMilestoneModal && (
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <label className="text-xs font-medium">Date</label>
                <Input type="date" className={editMilestoneModal.date ? 'date-filled' : ''}
                  value={editMilestoneModal.date}
                  onChange={e => setEditMilestoneModal(m => ({ ...m, date: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Time</label>
                <Input type="time"
                  value={editMilestoneModal.time}
                  onChange={e => setEditMilestoneModal(m => ({ ...m, time: e.target.value }))} />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setEditMilestoneModal(null)}>Cancel</Button>
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-white"
              onClick={saveMilestoneEdit} disabled={!editMilestoneModal?.date}>
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={foalingDeliveryModal} onOpenChange={setFoalingDeliveryModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delivery — Record Details</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {/* Date + Time on same row */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Delivery Date</Label>
                <Input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} className="h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Time</Label>
                <Input type="time" value={deliveryTime} onChange={e => setDeliveryTime(e.target.value)} className="h-8 text-xs" />
              </div>
            </div>

            {/* Pregnancy Status */}
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Pregnancy Status</Label>
              <Select value={deliveryPregnancyStatus || '__none__'} onValueChange={v => {
                setDeliveryPregnancyStatus(v === '__none__' ? '' : v);
                if (v !== 'Live Foal') { setFoalSex(''); setCreateFoalProfile(false); }
              }}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Select —</SelectItem>
                  <SelectItem value="Live Foal">Live Foal</SelectItem>
                  <SelectItem value="Stillborn">Stillborn</SelectItem>
                  <SelectItem value="Aborted">Aborted</SelectItem>
                  <SelectItem value="Slipped">Slipped</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Foal Sex — only if Live Foal */}
            {deliveryPregnancyStatus === 'Live Foal' && (
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Foal Sex</Label>
                <Select value={foalSex || '__none__'} onValueChange={v => setFoalSex(v === '__none__' ? '' : v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    <SelectItem value="m">Colt (Male)</SelectItem>
                    <SelectItem value="f">Filly (Female)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Create horse profile — only if Live Foal */}
            {deliveryPregnancyStatus === 'Live Foal' && (
              <div className="flex items-center gap-2 pt-1 border-t">
                <Checkbox
                  id="create-foal-profile"
                  checked={createFoalProfile}
                  onCheckedChange={setCreateFoalProfile}
                />
                <label htmlFor="create-foal-profile" className="text-xs cursor-pointer">
                  Create Horse Profile for Foal
                  <span className="block text-[10px] text-muted-foreground">
                    Dam: {horse.system_name || horse.name}{season.covering_sires ? ` · Sire: ${season.covering_sires}` : ''}
                  </span>
                </label>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={() => setFoalingDeliveryModal(false)}>Cancel</Button>
            <Button size="sm" onClick={saveFoalDelivery} className="bg-primary hover:bg-primary/90 text-white">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cover Record Dialog — opened when "Bred" is clicked or B chip is clicked */}
      <CoverRecordDialog
        open={coverDialogOpen}
        onClose={() => { setCoverDialogOpen(false); setCoverDialogRecord(null); setCoverDialogInitialDate(null); setCoverDialogShowTypeSelector(false); }}
        soldInfo={horse.sale_status === 'Sold' && !horse.keep_in_care ? { sold_date: horse.sold_date || null } : null}
        initialDate={coverDialogInitialDate}
        showTypeSelector={coverDialogShowTypeSelector}
        onTypeChange={(newType) => {
          // User switched away from Bred — open AddBreedingEventModal with the new type and same date
          setCoverDialogOpen(false);
          setCoverDialogShowTypeSelector(false);
          setEventModal({ open: true, type: newType, editing: null, date: coverDialogInitialDate });
        }}
        onSuccess={async (coverData) => {
          // Update season covering_sires if new sire was added
          if (!coverDialogRecord && coverData?.covering_sire) {
            try {
              const existingSires = (season.covering_sires || '').split(',').map(s => s.trim()).filter(Boolean);
              if (!existingSires.includes(coverData.covering_sire)) {
                const newSires = [...existingSires, coverData.covering_sire].join(', ');
                await api.put(`/breeding-seasons/${season.id}`, { covering_sires: newSires });
              }
            } catch { /* non-fatal */ }
          }
          setCoverDialogOpen(false);
          setCoverDialogRecord(null);
          fetchAll();
          onSeasonUpdated?.();
          onCoverAdded?.();
        }}
        horse={horse}
        record={coverDialogRecord}
        isFarm={isFarm}
      />
    </div>
  );
}
